import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  User, Mail, Phone, MapPin, Camera, Save, Edit, Award, Star, 
  Briefcase, Calendar, Clock, CheckCircle, Shield, Truck, Package,
  Upload, Loader2, Car, Shirt
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api, { filesAPI } from '../../services/api';

// Service type badge colors and icons
const serviceTypeBadges = {
  'rides': { color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Car, label: 'Rides' },
  'laundry': { color: 'bg-[#F4C2C2]/15 text-pink-700 border-pink-300', icon: Shirt, label: 'Laundry' },
  'errands': { color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Package, label: 'Errands' },
  'courier': { color: 'bg-[var(--success)]/15 text-green-700 border-green-300', icon: Truck, label: 'Courier' },
};

// Achievement badges
const achievementBadges = [
  { id: 'founder', label: 'Founder', icon: Star, color: 'bg-[var(--warning)]/15 text-yellow-700', description: 'Business owner' },
  { id: 'verified', label: 'Verified', icon: Shield, color: 'bg-[var(--success)]/15 text-green-700', description: 'Verified business' },
  { id: 'top_rated', label: 'Top Rated', icon: Award, color: 'bg-[#F4C2C2]/15 text-purple-700', description: '4.5+ average rating' },
  { id: 'trusted', label: 'Trusted', icon: CheckCircle, color: 'bg-[#D4AF37]/10 text-[#D4AF37]', description: '100+ completed jobs' },
];

const OwnerProfileTab = ({ user }) => {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [stats, setStats] = useState({
    total_customers: 0,
    total_workers: 0,
    total_bookings: 0,
    total_revenue: 0
  });
  const [profile, setProfile] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || 'Tarboro',
    state: user?.state || 'NC',
    zip_code: user?.zip_code || '27886',
    bio: user?.bio || '',
    profile_photo: user?.profile_photo || '',
    business_name: 'NC Help Hub',
    services_offered: ['rides', 'laundry', 'errands', 'courier'],
    badges: ['founder', 'verified']
  });

  // Handle profile picture upload
  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'photo');
      formData.append('destination', 'profile_photos');
      formData.append('label', 'Profile Photo');
      formData.append('description', 'Owner profile picture');

      const response = await api.post('/uploads/universal', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = response.data.file_url;
      
      // Update local state
      setProfile(prev => ({ ...prev, profile_photo: fileUrl }));
      
      // Auto-save to database so user doesn't have to click Save
      await api.put('/auth/profile', { profile_photo: fileUrl });
      
      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [analyticsRes] = await Promise.all([
        api.get('/analytics/dashboard').catch(() => ({ data: {} }))
      ]);
      if (analyticsRes.data) {
        setStats({
          total_customers: analyticsRes.data.customers?.total || 0,
          total_workers: analyticsRes.data.workers?.total || 0,
          total_bookings: analyticsRes.data.bookings?.total || 0,
          total_revenue: analyticsRes.data.payments?.total_revenue || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put('/auth/profile', {
        full_name: profile.full_name,
        phone: profile.phone,
        address: profile.address,
        city: profile.city,
        state: profile.state,
        zip_code: profile.zip_code,
        bio: profile.bio,
        profile_photo: profile.profile_photo
      });
      toast.success('Profile updated successfully!');
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="owner-profile-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">My Profile</h1>
          <p className="text-[var(--text-muted)]">Manage your profile and business information</p>
        </div>
        {!editMode ? (
          <Button onClick={() => setEditMode(true)} variant="outline">
            <Edit className="w-4 h-4 mr-2" /> Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={loading} className="btn-primary">
              <Save className="w-4 h-4 mr-2" /> {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="card p-6 text-center">
            {/* Profile Photo */}
            <div className="relative inline-block mb-4">
              <div className="w-32 h-32 mx-auto bg-gradient-to-br from-[var(--primary)] to-pink-400 rounded-full flex items-center justify-center overflow-hidden">
                {profile.profile_photo ? (
                  <img 
                    src={filesAPI.getUrl(profile.profile_photo)} 
                    alt="Profile" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <span className="text-4xl font-bold text-white">
                    {profile.full_name?.charAt(0)?.toUpperCase() || 'O'}
                  </span>
                )}
              </div>
              {/* Camera button to trigger upload - always visible on the profile photo */}
              <button 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute bottom-0 right-0 w-10 h-10 bg-[var(--background-paper)] rounded-full shadow-lg flex items-center justify-center border-2 border-[var(--primary)] hover:bg-[var(--primary)] hover:text-white transition-colors"
                title="Change profile photo"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 text-[var(--primary)] animate-spin" />
                ) : (
                  <Camera className="w-5 h-5 text-[var(--primary)]" />
                )}
              </button>
              {/* Hidden file input for profile photo upload */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleProfilePictureUpload}
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
              />
            </div>

            <h2 className="text-xl font-bold mb-1">{profile.full_name}</h2>
            <p className="text-[var(--text-muted)] mb-2">{profile.business_name}</p>
            
            {/* Role Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-[var(--primary)] to-pink-500 text-white rounded-full text-sm font-medium mb-4">
              <Star className="w-4 h-4 fill-white" />
              Owner
            </div>

            {/* Achievement Badges */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-[var(--text-secondary)] mb-3">Achievements</p>
              <div className="flex flex-wrap justify-center gap-2">
                {profile.badges?.map(badgeId => {
                  const badge = achievementBadges.find(b => b.id === badgeId);
                  if (!badge) return null;
                  return (
                    <div 
                      key={badgeId}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${badge.color}`}
                      title={badge.description}
                    >
                      <badge.icon className="w-3.5 h-3.5" />
                      {badge.label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Services Offered */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-[var(--text-secondary)] mb-3">Services Offered</p>
              <div className="flex flex-wrap justify-center gap-2">
                {profile.services_offered?.map(serviceId => {
                  const service = serviceTypeBadges[serviceId];
                  if (!service) return null;
                  return (
                    <div 
                      key={serviceId}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${service.color}`}
                    >
                      <service.icon className="w-3.5 h-3.5" />
                      {service.label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="card p-4 mt-4">
            <h3 className="font-semibold mb-3">Business Overview</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-[#D4AF37]/10 rounded-lg">
                <p className="text-2xl font-bold text-[#D4AF37]">{stats.total_customers}</p>
                <p className="text-xs text-[#D4AF37]/70">Customers</p>
              </div>
              <div className="text-center p-3 bg-[var(--success)]/10 rounded-lg">
                <p className="text-2xl font-bold text-[var(--success)]">{stats.total_workers}</p>
                <p className="text-xs text-[var(--success)]/70">Workers</p>
              </div>
              <div className="text-center p-3 bg-[#F4C2C2]/10 rounded-lg">
                <p className="text-2xl font-bold text-[#F4C2C2]">{stats.total_bookings}</p>
                <p className="text-xs text-[#F4C2C2]/70">Bookings</p>
              </div>
              <div className="text-center p-3 bg-[var(--warning)]/10 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.total_revenue)}</p>
                <p className="text-xs text-amber-600/70">Revenue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <h3 className="font-semibold mb-4">Profile Information</h3>
            
            <div className="space-y-4">
              {/* Name */}
              <div>
                <Label className="flex items-center gap-2"><User className="w-4 h-4" /> Full Name</Label>
                {editMode ? (
                  <Input 
                    value={profile.full_name}
                    onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                    className="mt-1"
                  />
                ) : (
                  <p className="mt-1 text-[var(--text-primary)]">{profile.full_name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
                <p className="mt-1 text-[var(--text-primary)]">{profile.email}</p>
                <p className="text-xs text-[var(--text-muted)]">Email cannot be changed</p>
              </div>

              {/* Phone */}
              <div>
                <Label className="flex items-center gap-2"><Phone className="w-4 h-4" /> Phone</Label>
                {editMode ? (
                  <Input 
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    className="mt-1"
                    placeholder="(252) 555-0100"
                  />
                ) : (
                  <p className="mt-1 text-[var(--text-primary)]">{profile.phone || 'Not set'}</p>
                )}
              </div>

              {/* Address */}
              <div>
                <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Business Address</Label>
                {editMode ? (
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <Input 
                      value={profile.address}
                      onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                      placeholder="Street Address"
                      className="col-span-2"
                    />
                    <Input 
                      value={profile.city}
                      onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                      placeholder="City"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input 
                        value={profile.state}
                        onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                        placeholder="State"
                      />
                      <Input 
                        value={profile.zip_code}
                        onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })}
                        placeholder="ZIP"
                      />
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-[var(--text-primary)]">
                    {profile.address ? `${profile.address}, ` : ''}{profile.city}, {profile.state} {profile.zip_code}
                  </p>
                )}
              </div>

              {/* Bio */}
              <div>
                <Label className="flex items-center gap-2"><Briefcase className="w-4 h-4" /> About / Bio</Label>
                {editMode ? (
                  <Textarea 
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    className="mt-1"
                    rows={4}
                    placeholder="Tell customers about your business..."
                  />
                ) : (
                  <p className="mt-1 text-[var(--text-primary)]">{profile.bio || 'No bio added yet'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Preferences */}
          <div className="card p-6 mt-4">
            <h3 className="font-semibold mb-4">Contact Display</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              This information appears on your forms and emails to customers:
            </p>
            <div className="bg-[var(--background-subtle)] rounded-lg p-4 border">
              <p className="font-semibold text-lg">{profile.business_name}</p>
              <p className="text-[var(--text-secondary)]">{profile.city}, {profile.state} {profile.zip_code}</p>
              <p className="text-[var(--primary)]">{profile.email}</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Export the service type badges for use in other components
export { serviceTypeBadges, achievementBadges };
export default OwnerProfileTab;
